// Patient Ledger Drawer — unified surface for the right-side detail pane.
//
// Hosts both eligibility content (Eligibility tab) and patient money
// content (Ledger tab) in a single drawer. The Eligibility tab is the
// successor to the standalone EligibilityDetailDrawer; the Ledger tab
// replaces PaymentRequestModal / RecordPaymentModal / WriteOffModal /
// CaptureChargesModal / LogCollectionForm.
//
// Wrapped by SlideSidePane (the production drawer chrome — resizable,
// click-outside-to-close, back-arrow + close X). Mounted by
// PatientLedgerDrawerHost from a single Redux slice — no modal stack
// involvement.
//
// "Log collection" inside the Eligibility tab flips this drawer to the
// Ledger tab + opens the settle sheet (method=external). No drawer hop,
// no nested side panes.

import { useEffect, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useSubjectLanguageVariants } from '@features/user';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import SlideSidePane from '../../Views/Shared/SlideSidePane';

import { AddChargeSheet } from './components/AddChargeSheet';
import { EligibilityTabContent } from './components/EligibilityTabContent';
import { LedgerTabContent } from './components/LedgerTabContent';
import { SettleSheet } from './components/SettleSheet';
import {
  settleAmountFor,
  useSettleSheetProps,
} from './hooks/useSettleSheetProps';
import type {
  LedgerDrawerProps,
  LedgerInitialAction,
  LedgerTab,
  SettleMethod,
} from './types';

interface DrawerProps extends LedgerDrawerProps {
  onClose: () => void;
}

const PatientLedgerDrawer = (props: DrawerProps) => {
  const { payment } = useEventTracking();
  // GDN-2944: gated, this tab is the action surface, not "the ledger" —
  // the ledger lives on the page (Activity table) and in the
  // account-history document.
  const showBalanceTable = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);
  const { singularCap } = useSubjectLanguageVariants();

  const isEncounter = !!props.encounterId;
  const [tab, setTab] = useState<LedgerTab>(
    props.initialTab ?? (isEncounter ? 'eligibility' : 'ledger'),
  );
  const [activeSheet, setActiveSheet] = useState<LedgerInitialAction | null>(
    props.initialAction ?? null,
  );

  // The drawer host keeps a single mounted instance and updates its props
  // when another eligibility/ledger trigger fires. Honor those destination
  // props after mount so in-drawer actions like "Invoice Client" can flip
  // from Eligibility to Ledger and open the requested sheet.
  useEffect(() => {
    setTab(props.initialTab ?? (isEncounter ? 'eligibility' : 'ledger'));
    setActiveSheet(props.initialAction ?? null);
  }, [
    isEncounter,
    props.encounterId,
    props.initialAction,
    props.initialTab,
    props.patientId,
  ]);

  // Ledger read + settle-sheet prop assembly. Shared with the
  // page-anchored PaymentSheetHost (gdn-2957) so the two surfaces can
  // never drift on which lines are invoiceable, writable, or credited.
  const { ledger, sheet } = useSettleSheetProps({
    patientId: props.patientId,
    encounterId: props.encounterId,
    chargeLineIds: props.chargeLineIds,
  });

  // Continuity of analytics with the modals being deprecated. Fires once
  // per open. The previous PaymentRequestModal/RecordPaymentModal/etc.
  // each emitted their own modalOpened — this is the consolidated equivalent.
  useEffect(() => {
    payment.modalOpened({ source: props.source ?? 'ledger_drawer' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const balance = ledger.balanceCents;
  const isSettled = balance === 0;
  const tone: 'green' | 'amber' = isSettled ? 'green' : 'amber';
  const toneColor = ELIG_TOKENS[tone];
  const patientName = props.patientName ?? ledger.patientName ?? '';
  const subtitle = isEncounter ? props.encounterLabel ?? 'Encounter' : 'All visits';
  const titleLine = patientName
    ? `${patientName}${subtitle ? ` · ${subtitle}` : ''}`
    : `${singularCap}${subtitle ? ` · ${subtitle}` : ''}`;

  const flipToLedgerSettleExternal = () => {
    setTab('ledger');
    setActiveSheet({ kind: 'settle', method: 'external' });
  };

  return (
    <SlideSidePane open onClose={props.onClose} title={titleLine}>
      {/* Strip CardContent's default padding so we control spacing fully,
          and provide a positioned column container so sheets can slide up
          from the bottom anchored to the visible drawer area. */}
      <Box
        sx={{
          m: '-16px',
          height: 'calc(100% + 32px)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: ELIG_TOKENS.bg,
        }}
      >
        <LedgerHero
          balance={balance}
          isSettled={isSettled}
          toneColor={toneColor}
          loading={ledger.isLoading}
        />

        <TabStrip value={tab} onChange={setTab} moneyTabLabel={showBalanceTable ? 'Billing' : 'Ledger'} />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {tab === 'eligibility' ? (
            <EligibilityTabContent
              patientId={props.patientId}
              encounterId={props.encounterId ?? undefined}
              patientName={patientName}
              source={props.source}
              initialForm={props.initialEligibilityForm}
              onLogCollection={flipToLedgerSettleExternal}
            />
          ) : (
            <LedgerTabContent
              patientId={props.patientId}
              entries={ledger.entries}
              activityLines={ledger.activityLines}
              encounter={ledger.encounter}
              preview={ledger.preview}
              openInvoices={ledger.openInvoices}
              staleInvoiceIds={ledger.staleInvoiceIds}
              isEncounterScope={isEncounter}
              showEncounterLabels={!isEncounter}
            />
          )}
        </Box>

        <DrawerFooter
          tab={tab}
          isSettled={isSettled}
          balance={balance}
          toneColor={toneColor}
          hasOpenInvoice={ledger.openInvoiceIds.length > 0}
          onSettle={(method) => setActiveSheet({ kind: 'settle', method })}
          onAddCharge={() => setActiveSheet({ kind: 'add_charge' })}
        />

        {activeSheet?.kind === 'settle' && (
          <SettleSheet
            // Re-seed the sheet when a new trigger lands a different preset
            // (e.g. toolbar copay route while the sheet is already open) —
            // destination-surface semantics over preserved half-typed state.
            key={`${activeSheet.method ?? 'default'}:${activeSheet.paymentIntent ?? 'general'}`}
            patientId={props.patientId}
            patientName={patientName}
            encounterId={props.encounterId}
            contextVisitStartsAt={props.contextVisitStartsAt}
            initialMethod={activeSheet.method}
            initialPaymentIntent={activeSheet.paymentIntent}
            initialAmountCents={settleAmountFor(
              activeSheet.method,
              props.defaultAmountCents,
              balance,
            )}
            preselectChargeLines={activeSheet.preselectLines ?? true}
            // BE-synthesized invoice reason from /preview — pre-fills the
            // SettleSheet's "Reason (shown on invoice)" field for the
            // invoice / card_on_file methods. User can still edit.
            suggestedReason={sheet.suggestedReason}
            invoiceChargeLineIds={sheet.invoiceChargeLineIds}
            writableChargeLineIds={sheet.writableChargeLineIds}
            invoiceableAmountCents={sheet.invoiceableAmountCents}
            unallocatedCreditCents={sheet.unallocatedCreditCents}
            chargeGroups={sheet.chargeGroups}
            chargeGroupsLoading={sheet.chargeGroupsLoading}
            reminderInvoiceId={sheet.reminderInvoiceId}
            copaySuggestion={sheet.copaySuggestion}
            source={props.source}
            onClose={() => setActiveSheet(null)}
          />
        )}
        {activeSheet?.kind === 'add_charge' && (
          <AddChargeSheet
            patientId={props.patientId}
            encounterId={props.encounterId}
            onClose={() => setActiveSheet(null)}
          />
        )}
      </Box>
    </SlideSidePane>
  );
};

// ─── Convergent balance signal ───────────────────────────────────────────────

const LedgerHero = ({
  balance,
  isSettled,
  toneColor,
  loading,
}: {
  balance: number;
  isSettled: boolean;
  toneColor: string;
  loading: boolean;
}) => {
  const label = isSettled ? 'Settled' : balance < 0 ? 'Credit' : 'Outstanding';
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: ELIG_TOKENS.card,
      }}
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          flexShrink: 0,
          ...(isSettled
            ? { bgcolor: toneColor }
            : { border: `2px solid ${toneColor}`, bgcolor: 'transparent' }),
        }}
      />
      <Typography
        variant="h5"
        sx={{ color: toneColor, fontWeight: 600, lineHeight: 1 }}
      >
        {label} {loading ? '—' : dollars(balance)}
      </Typography>
    </Box>
  );
};

// ─── Tab strip (encounter scope only) ────────────────────────────────────────

const TabStrip = ({
  value,
  onChange,
  moneyTabLabel,
}: {
  value: LedgerTab;
  onChange: (v: LedgerTab) => void;
  moneyTabLabel: string;
}) => (
  <Tabs
    value={value}
    onChange={(_, v) => onChange(v as LedgerTab)}
    sx={{
      px: 3,
      borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      minHeight: 40,
      bgcolor: ELIG_TOKENS.card,
      '& .MuiTab-root': {
        minHeight: 40,
        textTransform: 'none',
        fontWeight: 600,
        color: ELIG_TOKENS.inkSoft,
        '&.Mui-selected': { color: ELIG_TOKENS.midnight },
      },
      '& .MuiTabs-indicator': { bgcolor: ELIG_TOKENS.midnight },
    }}
  >
    <Tab value="eligibility" label="Eligibility" />
    <Tab value="ledger" label={moneyTabLabel} />
  </Tabs>
);

// ─── Footer ──────────────────────────────────────────────────────────────────

const DrawerFooter = ({
  tab,
  isSettled,
  balance,
  toneColor,
  hasOpenInvoice,
  onSettle,
  onAddCharge,
}: {
  tab: LedgerTab;
  isSettled: boolean;
  balance: number;
  toneColor: string;
  hasOpenInvoice: boolean;
  onSettle: (method: SettleMethod) => void;
  onAddCharge: () => void;
}) => {
  // Eligibility tab keeps its own affordances (Verify benefits / Log
  // collection) inside the tab content. Footer only renders for Ledger tab.
  if (tab === 'eligibility') return null;

  return (
    <Box
      sx={{
        borderTop: `1px solid ${ELIG_TOKENS.line}`,
        bgcolor: ELIG_TOKENS.card,
        px: 3,
        py: 1.5,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
      }}
    >
      <FooterTextButton onClick={onAddCharge}>+ Add charge</FooterTextButton>
      <Box sx={{ flex: 1 }} />
      {!isSettled && (
        <FooterPrimaryButton
          color={toneColor}
          onClick={() =>
            hasOpenInvoice ? onSettle('remind_invoice') : onSettle('cash')
          }
        >
          Settle {dollars(balance)}
        </FooterPrimaryButton>
      )}
    </Box>
  );
};

const FooterTextButton = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      bgcolor: 'transparent',
      border: 0,
      cursor: 'pointer',
      color: ELIG_TOKENS.inkSoft,
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      px: 1,
      py: 0.75,
      borderRadius: 1,
      '&:hover': { bgcolor: ELIG_TOKENS.line },
    }}
  >
    {children}
  </Box>
);

const FooterPrimaryButton = ({
  color,
  children,
  onClick,
}: {
  color: string;
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      bgcolor: color,
      color: '#fff',
      border: 0,
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      px: 2,
      py: 0.875,
      borderRadius: 1,
      '&:hover': { filter: 'brightness(0.9)' },
    }}
  >
    {children}
  </Box>
);

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  const sign = cents < 0 ? '−' : '';
  if (abs % 100 === 0) return `${sign}$${abs / 100}`;
  return `${sign}$${(abs / 100).toFixed(2)}`;
};

export default PatientLedgerDrawer;
